import React from 'react';
import type { FontWeight } from '@/components/utils/TypeUtils';
import { MapToWeight } from '@/components/utils/TypeUtils';

export interface ITextProps {
  /** Font weight */
  weight?: FontWeight;
  /** Specify how the text itself should be rendered. Make sure to apply all props to the root node and render children appropriately */
  as?:
    | keyof JSX.IntrinsicElements
    | React.ElementType
    | 'span'
    | 'p'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'div';
  /** Specify the content of your Text. */
  children: React.ReactNode;
  /** Specify an optional className to be added to your Text */
  className?: string;
  /** Specify an optional id to be added to your Text */
  id?: string;
  /** Text decoration */
  decoration?: 'underline' | 'line-through' | 'inherit' | 'none';
  /** Text transform */
  transform?: 'uppercase' | 'capitalize' | 'lowercase' | 'inherit' | 'none';
  /** Color of the Text. */
  color?: string;
  /** Applies font size. */
  size?: number;
  /** Title of the Text. */
  title?: string;
}

/**
 * Text is a style component that renders a string of characters, words, or paragraphs in a consistent font size and weight.
 */
export const Text: React.FC<ITextProps> = ({
  weight = 'light',
  as = 'div',
  transform = 'inherit',
  decoration = 'inherit',
  children,
  className,
  color,
  size,
  id,
  ...rest
}) =>
  React.createElement(
    as,
    {
      className,
      id,
      ...rest,
      style: {
        fontWeight: MapToWeight[weight],
        textTransform: transform,
        textDecoration: decoration,
        fontSize: size,
        color,
      },
    },
    children
  );
